import {LitElement, html, css} from 'lit';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {connect} from 'pwa-helpers';
import {
	Scene,
	WebGLRenderer,
	PerspectiveCamera,
	OrthographicCamera,
	MeshBasicMaterial,
	Mesh,
	Vector2,
	Vector3,
	Raycaster,
	DirectionalLight,
	AmbientLight,
	BoxGeometry,
	Fog,
	PCFSoftShadowMap,
	VSMShadowMap,

	NoToneMapping,
	ReinhardToneMapping,
	CineonToneMapping,
	ACESFilmicToneMapping,
	AgXToneMapping,
} from 'three';
import {OBB} from 'three/examples/jsm/math/OBB.js';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls.js';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js';
import {HDRCubeTextureLoader} from 'three/examples/jsm/loaders/HDRCubeTextureLoader.js';
import {FontLoader} from 'three/examples/jsm/loaders/FontLoader.js';

import {cssVars, listen} from 'nuffjs';

import store from '../../../store';
import {getValueOfFeature} from '../../../util/values';
import {
	selectAvailableDimmingLevel,
	selectColorTemperature,
	selectDimmingLevel,
	selectLumen,
	selectProductData,
	selectSpcConfiguration,
	selectCurrentSection,
	selectSectionConfig,
	selectDebug,
	selectImages
} from '../../../selectors';
import {getProductData} from '../../../util/getProductData';
// import {stringToAttributeId} from '../../../components/spc/util/spcUtils';

import {degToRad, getColorSimpleBrightness, getQuaternionByAngle} from './olivio3d-utils';
import {
	selectOlivioBracket, selectOlivioCandelabraTop, selectOlivioCandelabraTopIsSatisfied, selectOlivioDesign, selectOlivioHeads,
	selectOlivioHousingColourHexValue, selectOlivioHousingColourInlay, selectOlivioHousingColourInlayHexValue, selectOlivioHousingColourPoleHexValue,
	selectOlivioHousingSize,
	selectOlivioLayout, selectOlivioMaxAngle, selectOlivioMaxHeight, selectOlivioMinAngle, selectOlivioMinHeight,
	selectOlivioNumberOfHeads,
	selectOlivioNumberOfHeadsValue,
	selectOlivioPoleHeight,
	selectOlivioPoleHeightValue, selectOlivioSelectedHead,
	selectOlivioWoodType, selectOlivioPoleDiameter, selectOlivioPoleMaterial
} from '../../olivioSelectors';

import {setSelectedHead, setOlivioHeadsData} from '../../olivioSlice';

import config from '../../../config';
import {isSectionSatisfied} from '../../../util/sectionTabInfo.js';
import {olivioLuminaires as luminaires} from '../../luminaires/olivioLuminaires.js';
import {designsConfigs} from './olivio3d-designsConfigs.js';

import iconTopView from '../../../../icons/top-view.js';
import iconSideView from '../../../../icons/side-view.js';
import icon3dView from '../../../../icons/3d-view.js';
import iconFlatView from '../../../../icons/flat-view.js';
import iconRotationOn from '../../../../icons/rotation-on.js';
import iconRotationOff from '../../../../icons/rotation-off.js';
import iconAutoView from '../../../../icons/auto-view.js';
import iconManualView from '../../../../icons/manual-view.js';
import {setSection} from '../../../slices/statusSlice.js';
import {saveCanvasImage} from '../../../slices/configurationSlice.js';

const conf = {
	maxHeadsAmount: 8,

	detailAngle: 180, // deg

	// camera config
	detailVerticalOffset: -0.30,

	// limits for camera movements
	cameraConstraints: {
		distance: [2, 50],
		verticalRotation: [degToRad(10), degToRad(160)], // 0 = looking down, 180 = looking up
		detailDistance: 1.6
	},

	cameraPositions: {
		sistema: {
			side: (configValues) => {
				const poleHeight = +configValues.poleHeight;
				// https://www.wolframalpha.com/input?i=quadratic+fit+%7B3%2C+2%7D%2C+%7B4%2C+2.7%7D%2C+%7B6%2C+4.2%7D%2C+%7B8%2C+5.5%7D%2C+%7B10%2C+7%7D%2C+%7B12%2C+8%7D
				const y = -0.0115363 * poleHeight**2 + 0.850052 * poleHeight - 0.482557;
				const z = poleHeight * 0.98;
				return [0, y, z];
			},
			top: (configValues) => {
				const poleHeight = +configValues.poleHeight;
				return [0, poleHeight + 1, 0.001];
			},
		},

		floracion: {
			side: (configValues) => (conf.cameraPositions.sistema.side(configValues)),
			top: (configValues) => (conf.cameraPositions.sistema.top(configValues)),
		},

		candelabra: {
			side: (configValues) => ([0, +configValues.poleHeight === 4.5 ? 3.6 : 3, 4.5]),
			top: (configValues) => (conf.cameraPositions.sistema.top(configValues)),
		},

		bollard: {
			side: (configValues) => ([0, 1, 4.5]),
			top: (configValues) => (conf.cameraPositions.sistema.top(configValues)),
		},

		universal: {
			side: (configValues) => ([0, 2.8, 2]),
			top: (configValues) => ([0, 4, 0.001]),
		},

		designs: {
			pos: [0, 6.5, 7.7],
			dir: [0, 4.8, 0]
		}
	},

	clickMaxDistance: 20,
	clickMaxDelay: 200,
	cursorVar: 'cursor',

	detailModeSectionName: 'olivioHeadDetail',
	introSectionName: 'olivioStart',
	designsSectionName: 'olivioDesignSelect',

	hoverColor: '#BBBBBB',
	boxHelperColor: 0x008800,
	boxHelperOnCollisionColor: 0xFF0000,

	staticViewSide: 'side',
	staticViewTop: 'top',
	staticViewSideHeight: 12.3,
	sceneBgProp: 'sceneBg',

	cameraTypePerspective: 'perspective',
	cameraTypeOrthographic: 'orthographic',
	cameraTypeDesigns: 'designs',
	orthographicThreshold: -0.997,
};

@customElement('olivio-3d')
export class Olivio3d extends connect(store)(LitElement) {

	static styles = css`
		:host {
			--cursor: default;
			display: flex;
			width: 100%;
			height: 100%;
			overflow: hidden;
			position: relative;
			cursor: var(--cursor, default);
			background-color: var(--sceneBg, transparent);
		}

		.container {
			width: 100%;
		}

		.controls {
			margin-right: var(--large-padding);
			pointer-events: none;
			position: absolute;
			right: 0;
			top: 50%;
			transform: translateY(-50%);
			/* border: 1px solid white; */
			display: flex;
			flex-direction: column;
			gap: 8px;
		}

		.action {
			pointer-events: auto;
			cursor: pointer;
			border: 0;
			background-color: transparent;
			display: flex;
			appearance: none;
			color: var(--color-white);
			justify-content: flex-end;
			align-items: center;
		}

		.actionLabel {
			margin-right: 8px;
			visibility: hidden;
			pointer-events: none;
			opacity: 0;
			transition: opacity 0.2s ease-in-out;
		}

		.action:hover .actionLabel,
		.action:focus-visible .actionLabel {
			visibility: inherit;
			opacity: 1;
		}

		.actionIcon {
			width: 50px;
			height: 48px;
			background-color: var(--color-grey7);
			display: inline-flex;
			justify-content: center;
			align-items: center;
			color: var(--color-white);
			transition: background-color 0.2s ease-in-out;
		}
		.actionIcon:hover {
			background-color: var(--color-grey6);
		}

		.numbers {
			pointer-events: none;
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			z-index: 3;
		}

		.number {
			font: var(--font-widget-label);
			border: 1px solid var(--color-white);
			border-radius: 10px;
			height: 20px;
			width: 20px;
			display: flex;
			align-items: center;
			justify-content: center;
			color: var(--color-white);
			position: absolute;
			left: 0;
			top: 0;
			opacity: 0;
			transition: opacity 0.2s ease-in-out;
		}

		.number:nth-child(1) {
			transform: translate(var(--number1x, 0), var(--number1y, 0)) translate(-50%, -50%);
			opacity: var(--number1opacity, 0);
		}
		.number:nth-child(2) {
			transform: translate(var(--number2x, 0), var(--number2y, 0)) translate(-50%, -50%);
			opacity: var(--number2opacity, 0);
		}
		.number:nth-child(3) {
			transform: translate(var(--number3x, 0), var(--number3y, 0)) translate(-50%, -50%);
			opacity: var(--number3opacity, 0);
		}
		.number:nth-child(4) {
			transform: translate(var(--number4x, 0), var(--number4y, 0)) translate(-50%, -50%);
			opacity: var(--number4opacity, 0);
		}
		.number:nth-child(5) {
			transform: translate(var(--number5x, 0), var(--number5y, 0)) translate(-50%, -50%);
			opacity: var(--number5opacity, 0);
		}
		.number:nth-child(6) {
			transform: translate(var(--number6x, 0), var(--number6y, 0)) translate(-50%, -50%);
			opacity: var(--number6opacity, 0);
		}
		.number:nth-child(7) {
			transform: translate(var(--number7x, 0), var(--number7y, 0)) translate(-50%, -50%);
			opacity: var(--number7opacity, 0);
		}
		.number:nth-child(8) {
			transform: translate(var(--number8x, 0), var(--number8y, 0)) translate(-50%, -50%);
			opacity: var(--number8opacity, 0);
		}

		.debug {
			color: var(--color-grey5);
			font: var(--font-widget-label);
			position: absolute;
			right: 0;
			bottom: 0;
			background-color: rgba(50, 50, 50, 0.3);
		}

		canvas {
			position: absolute;
			left: 0;
			top: 0;
		}
	`;

	static properties = {
		cameraAutoMode: {type: Boolean, state: true},
		showCollisionBoxes: {type: Boolean, state: true},
		autorotate: {type: Boolean, state: true},
		inDesignsScene: {type: Boolean, state: true},
		loaded: {type: Boolean, state: true},
		staticView: {type: String},
	};

	// three.js objects
	scene = null;
	designsScene = null;
	designsSceneFilled = false;
	renderer = null;
	camera = null;
	pointer = null;
	raycaster = null;

	luminaire = null;

	currentSection = null;
	currentDesign = null;
	selectingDesign = false;

	lights = [];
	lightTargets = [];

	poleTargetAngle = null;

	cameraPosition = null;
	cameraTargetPosition = null;
	designsCameraPosition = null;
	designsCameraTargetPosition = null;

	headBoxHelpers = [];
	bracketBoxHelpers = [];

	// the current pair bracket-head currently pointed by the user input
	pointedIndex = null;

	listeners = [];

	rect = null;

	// click detection
	downPos = null;
	downTime = null;

	lightOn = true;

	detailModeAllowed = false;

	updatingScene = Promise.resolve();

	loadingModels = Promise.resolve();

	threePreparing = null;

	lastCameraPosName = null;

	controlsInteractionStarted = false;

	lastTime = 0;

	forceCollisionChecks = false;

	cameraType = conf.cameraTypePerspective;

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
		this.cameraAutoMode = true;
		this.showCollisionBoxes = false;
		this.autorotate = true;
		this.inDesignsScene = false;
		this.loaded = false;
		this.pngSaved = {};
		this.staticView = false; //conf.staticViewTop;
	}

	connectedCallback() {
		super.connectedCallback();
		this.setupThreeEnv().then(() => {
			this.clearRenderer();
			this.setupEvents();
			this.lastTime = performance.now();
			this.animate();
		});
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.clearEvents();
	}

	// EVENTS HANDLERS

	onControlsStartInteraction(event) {
		this.controlsInteractionStarted = true;
	}

	onControlsChange(event) {
		if (this.controlsInteractionStarted) {
			this.controlsInteractionStarted = false;
			this.toggleCameraMode(false);
		}
	}

	onControlsEndInteraction(event) {
		this.controlsInteractionStarted = false;
	}


	onWindowResize() {
		const oldRect = this.rect;
		this.rect = this.getBoundingClientRect();
		if (this.rect.width !== oldRect.width || this.rect.height !== oldRect.height) {
			this.updateRendererGeometry();
			this.updateCameraGeometry();
			this.clearRenderer();
		}
	}

	onCaptureImages() {
		this.captureCanvasImage();
	}

	onPointerMove(event) {
		// normalize coordinates in the range [-1, 1]
		this.pointer.x = (event.clientX - this.rect.left) / this.rect.width * 2 - 1;
		this.pointer.y = -(event.clientY - this.rect.top) / this.rect.height * 2 + 1;
	}

	onPointerDown(event) {
		// save values to check on pointerUp, in order to detect a proper click, instead of a drag
		// gesture
		this.downPos = [event.clientX, event.clientY];
		this.downTime = Date.now();
	}

	onPointerUp(event) {
		if (!this.luminaire || !this.downPos || !this.downTime || this.pointedIndex === null) {
			return;
		}
		const downTime = Date.now();
		const diffX = this.downPos[0] - event.clientX;
		const diffY = this.downPos[1] - event.clientY;
		const distance = Math.sqrt(diffX ** 2 + diffY ** 2);
		const configValues = this.luminaire.getConfigValues();
		if (
			(downTime - this.downTime) <= conf.clickMaxDelay &&
			distance <= conf.clickMaxDistance &&
			(this.currentSection !== conf.detailModeSectionName || this.pointedIndex !== configValues.selectedHead)
		) {
			if (this.pointedIndex !== configValues.selectedHead) {
				store.dispatch(setSelectedHead({selectedHead: this.pointedIndex}));
			}
			if (this.currentSection !== conf.detailModeSectionName) {
				store.dispatch(setSection({currentSection: conf.detailModeSectionName}));
			}
		}
		this.downPos = null;
		this.downTime = null;
	}

	onPointerIn(index) {
		if (this.luminaire) {
			const configValues = this.luminaire.getConfigValues();
			if (
				(this.currentSection === conf.detailModeSectionName && index !== configValues.selectedHead) ||
				(this.currentSection !== conf.detailModeSectionName && this.detailModeAllowed)
			) {
				const DesignClass = this.luminaire.constructor;
				const branches = this.luminaire.getBranches();
				this.luminaire.setColor(
					DesignClass.singleBracket ? branches[index].head : branches[index].bracket,
					conf.hoverColor
				);
				cssVars(this).set(conf.cursorVar, 'pointer');
			}
		}
	}

	onPointerOut(index) {
		if (this.luminaire) {
			const configValues = this.luminaire.getConfigValues();
			if (this.currentSection === conf.detailModeSectionName || this.detailModeAllowed) {
				const DesignClass = this.luminaire.constructor;
				const branches = this.luminaire.getBranches();
				if (!DesignClass.singleBracket) {
					this.luminaire.setColor(branches[index].bracket, configValues.heads[index].collisions.length ?
						DesignClass.conf.modelOnCollisionColor :
						DesignClass.getBracketColor(configValues)
					);
				}
				this.luminaire.setColor(branches[index].head, configValues.heads[index].collisions.length ?
					DesignClass.conf.modelOnCollisionColor :
					DesignClass.getHeadColor(configValues)
				);
				cssVars(this).remove(conf.cursorVar);
			}
		}
	}

	onKeyDown(event) {
		// if (event.ctrlKey && event.altKey) {
		// 	const key = event.key;
		// }
	}

	onAction(event) {
		const target = event.target.closest('.action');
		const action = target.action;
		const params = target.actionParams;
		//console.log('action', action, params);
		this[action](params);
	}



	stateChanged(state) {
		this.state = state;
		this.lang = state.status.lang;
		this.region = state.status.region;
		this.translations = state.productData.translations;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.productData = selectProductData(state);

		this.detailModeAllowed = this.isDetailModeAllowed(state);
		const configValues = {
			currentSection: selectCurrentSection(state),
			design: selectOlivioDesign(state),
			poleHeight: selectOlivioPoleHeight(state),
			numberOfHeads: selectOlivioNumberOfHeadsValue(state),
			bracket: selectOlivioBracket(state),
			layoutType: selectOlivioLayout(state),
			housingSize: selectOlivioHousingSize(state),
			color: selectOlivioHousingColourHexValue(state),
			colorInlay: selectOlivioHousingColourInlayHexValue(state),
			colorPole: selectOlivioHousingColourPoleHexValue(state),
			maxHeight: selectOlivioMaxHeight(state),
			minHeight: selectOlivioMinHeight(state),
			maxAngle: selectOlivioMaxAngle(state),
			minAngle: selectOlivioMinAngle(state),
			lumen: selectDimmingLevel(state),
			colourTemperature: selectColorTemperature(state),
			heads: selectOlivioHeads(state),
			selectedHead: selectOlivioSelectedHead(state),
			candelabraTop: selectOlivioCandelabraTop(state),
			poleDiameter: selectOlivioPoleDiameter(state),
			poleMaterial: selectOlivioPoleMaterial(state),
			woodType: selectOlivioWoodType(state),
			staticView: !!this.staticView,
		};


		configValues.design = configValues.design.toLowerCase();
		// console.log('STATE CHANGED', configValues);

		if (!this.staticView && (configValues.currentSection === conf.designsSectionName || configValues.currentSection === conf.introSectionName)) {
			this.updatingScene = this.updatingScene.then(() => this.updateDesignsScene(configValues.design, configValues.currentSection));
		} else if (configValues.design) {
			this.updatingScene = this.updatingScene.then(() => this.updateScene(configValues));
		}
	}

	async updateScene(configValues) {
		await this.setupThreeEnv();
		await this.loadModels();
		const currentSectionChanged = this.currentSection !== configValues.currentSection;
		this.currentSection = configValues.currentSection;
		const wasInDesignsScene = this.inDesignsScene;
		this.inDesignsScene = false;
		const design = configValues.design;
		if (currentSectionChanged && wasInDesignsScene) {
			this.setCameraType(this.cameraType);
		}
		this.currentDesign = design;
		const DesignClass = luminaires[design];
		let designChanged = false;
		const first = this.luminaire === null;
		if (!this.luminaire || this.luminaire.constructor.designName !== design) {
			designChanged = true;
			if (this.luminaire) {
				const pole = this.luminaire.getPole();
				if (pole.parent === this.scene)
				this.scene.remove(pole);
				// this.luminaire.destroy();
			}
			this.luminaire = new DesignClass();
		}


		if (design.hasBrackets && !design.singleBracket) {
			if (first) {
				const verticalOffsets = DesignClass.calculateOffsets(configValues, configValues.heads);
				store.dispatch(setOlivioHeadsData({heads: verticalOffsets.map((head) => ({la: head.la}))}));
				return;
			}
		}

		const flags = this.luminaire.update(configValues);

		if (flags.poleChanged) {
			this.scene.add(this.luminaire.getPole());
		}

		if (this.staticView) {
			this.moveStaticCamera();
		} else {
			// move camera if needed
			if (currentSectionChanged || designChanged) {
				this.lastCameraPosName = null;
				this.cameraAutoMode = true;
			}
			if (this.cameraAutoMode) {
				if (DesignClass.hasBrackets && configValues.currentSection === conf.detailModeSectionName && (currentSectionChanged || flags.selectedHeadChanged) && configValues.selectedHead !== null) {
					if (
						(this.lastCameraPosName !== 'top' && this.lastCameraPosName !== 'side') ||
						(flags.selectedHeadChanged && configValues.selectedHead !== null)
					) {
						this.moveCamera({pos: 'detail', configValues: configValues});
					}
				} else if (
					(currentSectionChanged && configValues.currentSection !== conf.detailModeSectionName) ||
					flags.poleHeightChanged || designChanged ||
					(configValues.currentSection === conf.detailModeSectionName && flags.selectedHeadHeightChanged) ||
					(configValues.currentSection === conf.detailModeSectionName && configValues.selectedHead === null)
				) {
					this.moveCamera({pos: 'side', configValues: configValues});
				} else if (configValues.currentSection === conf.detailModeSectionName && flags.selectedHeadAngleChanged) {
					this.moveCamera({pos: 'top', configValues: configValues});
				}
			}
		}

		this.forceCollisionChecks = true;
	}


	async updateDesignsScene(design, newSection) {
		await this.setupThreeEnv();
		await this.loadModels();

		this.inDesignsScene = true;
		const sectionChanged = this.currentSection !== newSection;
		this.currentSection = newSection;
		const designChanged = this.currentSection === conf.designsSectionName && design !== this.currentDesign;
		if (designChanged) {
			this.selectingDesign = true;
		} else if (sectionChanged) {
			this.selectingDesign = false;
		}
		this.currentDesign = design;
		if (sectionChanged) {
			this.setCameraType(conf.cameraTypeDesigns);
		}
		// create the luminaires the first time
		const first = !this.designsSceneFilled;
		if (!this.designsSceneFilled) {
			this.fillDesignsScene();
			this.designsSceneFilled = true;
		}
		if (first || designChanged || sectionChanged) {
			this.moveDesignsCamera();
		}
	}

	fillDesignsScene() {
		for (const designName of Object.keys(designsConfigs)) {
			const entry = designsConfigs[designName];
			const ClassName = luminaires[designName];
			const luminaire = new ClassName(entry.configValues);
			const pole = luminaire.getPole();
			this.designsScene.add(pole);
			pole.position.set(...entry.position);
		}
	}



	// SETUP METHODS

	setupThreeEnv() {
		if (!this.threePreparing) {
			if (this.staticView) {
				cssVars(this).set(conf.sceneBgProp, '#ffffff');
			}
			this.threePreparing = new Promise(async (resolve) => {
				this.rect = this.getBoundingClientRect();
				this.setupRenderer();
				await Promise.all([
					this.setupScene(),
					this.loadFont(),
				]);
				this.setupLights();
				this.setupCamera();
				this.updateRendererGeometry();
				resolve();
			});
		}
		return this.threePreparing;
	}

	setupRenderer() {
		this.renderer = new WebGLRenderer({alpha: true, antialias: true});
		this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.shadowMap.enabled = true;
		this.renderer.shadowMap.type = PCFSoftShadowMap;
		this.renderer.toneMapping = CineonToneMapping; // ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 1;
		this.renderer.preserveDrawingBuffer = true;
		// NoToneMapping,
		// ReinhardToneMapping,
		// CineonToneMapping,
		// ACESFilmicToneMapping,
		// AgXToneMapping,
		// this.renderer.shadowMap.type = BasicShadowMap;
		// this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // default THREE.PCFShadowMap
		// PCFSoftShadowMap
		//VSMShadowMap
	}
	async setupScene() {
		this.scene = new Scene();
		this.designsScene = new Scene();

		/*
		this.skyCubeTexture = await new Promise((resolve) => {
			const loader = new HDRCubeTextureLoader();
			loader.setPath( '/line-assets/olivio3d/cubes/pisaHDR/' );
			loader.load([ 'px.hdr', 'nx.hdr', 'py.hdr', 'ny.hdr', 'pz.hdr', 'nz.hdr'], () => {
				resolve(loader);
			});
		});
		*/
		//this.scene.background = this.skyCubeTexture;
		//this.scene.environment = this.skyCubeTexture;

		this.setupCollisionBoxHelpers();

		// const floorMaterial = new MeshPhysicalMaterial({
		// 	clearcoat: 0,
		// 	clearcoatRoughness: 1,
		// 	metalness: 0,
		// 	roughness: 1,
		// 	color: 0x000000,
		// });
		// const floorGeometry = new BoxGeometry(20, 0.01, 20);
		// const floor = new Mesh(floorGeometry, floorMaterial);
		// this.scene.add(floor);
		// floor.position.set(0, 0, 0);
		// floor.receiveShadow = true;

		// this.scene.fog = new Fog( 0x000000, 10, 25);

		// const spotLight1 = new SpotLight(0xFFFF00);
		// const spotLight2 = new SpotLight(0xFF0000);
		// this.scene.add(spotLight1);
		// this.scene.add(spotLight2);
		// spotLight1.position.set(-1, 6.55, 0);
		// spotLight2.position.set(-0.8, 6.5, 0);
		// this.scene.add(spotLight1.target);
		// this.scene.add(spotLight2.target);
		// spotLight1.angle = degToRad(2);
		// spotLight2.angle = degToRad(2);
		// spotLight1.intensity = 10;
		// spotLight2.intensity = 10;
		// spotLight1.castShadow = true;
		// spotLight2.castShadow = true;
		// spotLight1.intensity = 10;
		// spotLight2.intensity = 10;
		// spotLight1.shadow.camera.near = 0.2;
		// spotLight2.shadow.camera.near = 0.2;
		// // spotLight1.shadow.mapSize.width = 2048 * 10; // default is 1024
		// // spotLight1.shadow.mapSize.height = 2048 * 10; // default is 1024
		// // spotLight2.shadow.mapSize.width = 2048 * 10; // default is 1024
		// // spotLight2.shadow.mapSize.height = 2048 * 10; // default is 1024

		// spotLight1.target.position.set(0, 6.55, 0);
		// spotLight2.target.position.set(0, 6.5, 0);
		// // const spotLightHelper = new SpotLightHelper(spotLight);
		// // this.scene.add(spotLightHelper);

		// const geometry = new BoxGeometry(0.02, 0.30, 0.30);
		// const material = this.getRealisticMaterial();
		// const sep = new Mesh(geometry, material);
		// sep.castShadow = true;
		// sep.receiveShadow = true;
		// this.scene.add(sep);
		// sep.position.set(-0.5, 6.5, 0);


		// ------------


		// const cubeGeo = new BoxGeometry(0.8, 0.8, 0.8);
		// const material = this.getFlatMaterial();
		// this.cube = new Mesh(cubeGeo, material);
		// this.scene.add(this.cube);
		// this.cube.position.set(-1, 7, 0);

		// // const geometry = this.cube.geometry;
		// const edges = new EdgesGeometry(this.cube.geometry);
		// const line = new LineSegments(edges, new LineBasicMaterial({ color: 0xffffff}));
		// // line.material.depthTest = false;
		// this.scene.add(line);
		// line.position.set(-1, 7, 0);
		// objects.push(child);

	}

	setupCollisionBoxHelpers() {
		for (let i = 0; i < conf.maxHeadsAmount; i++) {
			for (const prefix of ['head', 'bracket']) {
				const geometry = new BoxGeometry(1, 1, 1);
				const material = new MeshBasicMaterial({wireframe: true, color: conf.boxHelperColor});
				const helper = new Mesh(geometry, material);
				helper.visible = false;
				this[prefix + 'BoxHelpers'][i] = helper;
				this.scene.add(helper);
			}
		}
	}

	setupLights() {
		const light = new DirectionalLight(0xFFFFFF, 3);
		light.position.set(10, 24, 0);
		light.castShadow = true;
		light.shadow.mapSize.width = 1024;
		light.shadow.mapSize.height = 1024;
		light.shadow.bias = -0.001;
		this.scene.add(light);
		this.rightDirectionalLight = light;

		if (this.staticView) {
			light.position.set(0, 6.5, 9);
			light.intensity = 4;
		}

		const fillLight = new DirectionalLight(0x909090, 1);
		fillLight.position.set(-10, 6, 0);
		fillLight.castShadow = true;
		fillLight.shadow.bias = -0.001;
		this.scene.add(fillLight);
		this.leftDirectionalLight = fillLight;

		// increased from 0.5 to 0.9 after applying tone mapping. it seems more realistic
		const ambientLight = new AmbientLight(0xffffff, 1.2); // soft white light
		this.ambientLight = ambientLight;
		this.scene.add(ambientLight);

		const bottomLight = new DirectionalLight(0xFFFFFF, 0.5);
		bottomLight.position.set(-3, 0, 1);
		bottomLight.castShadow = false;
		// light.shadow.mapSize.width = 1024;
		// light.shadow.mapSize.height = 1024;
		// light.shadow.bias = -0.001;
		this.scene.add(bottomLight);
		this.scene.add(bottomLight.target);
		bottomLight.target.position.set(0, 100, 0);

		// ----

		const designsLight = new DirectionalLight(0xFFFFFF, 3);
		designsLight.position.set(10, 24, 0);
		designsLight.castShadow = true;
		designsLight.shadow.mapSize.width = 1024;
		designsLight.shadow.mapSize.height = 1024;
		designsLight.shadow.bias = -0.001;
		this.designsScene.add(designsLight);

		const designsFillLight = new DirectionalLight(0x909090, 1);
		designsFillLight.position.set(-10, 6, 0);
		designsFillLight.castShadow = true;
		designsFillLight.shadow.bias = -0.001;
		this.designsScene.add(designsFillLight);

		// increased from 0.5 to 0.9 after applying tone mapping. it seems more realistic
		const designsAmbientLight = new AmbientLight(0xffffff, 1.2); // soft white light
		this.designsAmbientLight = ambientLight;
		this.designsScene.add(designsAmbientLight);

		// const bottomLight = new DirectionalLight(0xFFFFFF, 0.5);
		// bottomLight.position.set(-3, 0, 1);
		// bottomLight.castShadow = false;
		// // light.shadow.mapSize.width = 1024;
		// // light.shadow.mapSize.height = 1024;
		// // light.shadow.bias = -0.001;
		// this.scene.add(bottomLight);
		// this.scene.add(bottomLight.target);
		// bottomLight.target.position.set(0, 100, 0);


		// for (let i = 0; i < 8; i++) {
		// 	const spotLight = new SpotLight(0xFFFFFF);
		// 	spotLight.angle = degToRad(30);
		// 	spotLight.castShadow = true;
		// 	spotLight.shadow.camera.near = 0.1;
		// 	spotLight.power = conf.lightPower;
		// 	spotLight.penumbra = 0.08;
		// 	spotLight.shadow.mapSize = new Vector2(2048, 2048);
		// 	// const target = new Object3D();
		// 	const targetGeometry = new SphereGeometry(0.1, 0.1, 0.1);
		// 	const targetMaterial = new MeshBasicMaterial({color: 0xffffff});
		// 	const target = new Mesh(targetGeometry, targetMaterial);
		// 	spotLight.target = target;
		// 	const pivot = new Object3D();
		// 	pivot.position.set(0, 0, 0);
		// 	pivot.add(target);
		// 	target.position.set(0, 0, 0);
		// 	target.visible = false;
		// 	this.lights[i] = spotLight;
		// 	this.lightTargets[i] = pivot;
		// }
	}

	setupCamera() {
		const ratio = this.rect.width / this.rect.height;
		let w = 1;
		let h = 1;
		if (ratio >= 1) {
			h = w / ratio;
		} else {
			w = h * ratio;
		}

		this.cameras = {
			perspective: new PerspectiveCamera(45, ratio, 0.1, 50),
			orthographic: new OrthographicCamera(
				-w / 2,
				w / 2,
				h / 2,
				-h / 2,
				0.1,
				50
			),
			designs:  new OrthographicCamera(
				-w / 2,
				w / 2,
				h / 2,
				-h / 2,
				0.1,
				50
			)
		};

		this.camera = this.staticView ? this.cameras.orthographic : this.cameras.perspective;

		this.camera.updateProjectionMatrix();
		this.camera.layers.enableAll();
		this.cameraControls = new OrbitControls(this.camera, this.renderer.domElement);
		const data = this.getCameraMovementData('side', {design: 'sistema', poleHeight: 12});
		this.camera.position.copy(data.pos);
		this.cameraControls.target.copy(data.dir);
		this.cameraPosition = data.pos;
		this.cameraTargetPosition = data.dir;
		const distance = this.cameraPosition.distanceTo(this.cameraTargetPosition);
		this.cameraZoom = 1 / (distance * 2);
		this.cameras.orthographic.zoom = this.cameraZoom;
		this.cameras.designs.zoom = this.cameraZoom;

		this.setCameraConstraints();

		if (this.staticView) {
			this.cameraControls.enabled = false;
		}

		this.pointer = new Vector2(-1, -1);
		this.raycaster = new Raycaster();
	}

	setupEvents() {
		this.listeners = [
			listen(window, 'captureImages', this.onCaptureImages.bind(this), {throttle: true}),
			listen(window, 'resize', this.onWindowResize.bind(this), {throttle: true}),
			listen(window, 'keydown', this.onKeyDown.bind(this)),
			listen(this, 'pointermove', this.onPointerMove.bind(this), {throttle: true}),
			listen(this, 'pointerdown', this.onPointerDown.bind(this)),
			listen(this, 'pointerup', this.onPointerUp.bind(this)),
			listen(this.cameraControls, 'start', this.onControlsStartInteraction.bind(this)),
			listen(this.cameraControls, 'change', this.onControlsChange.bind(this)),
			listen(this.cameraControls, 'end', this.onControlsEndInteraction.bind(this)),
		];
	}

	clearEvents() {
		for (const listener of this.listeners) {
			listener.unlisten();
		}
	}

	loadFont() {
		const loader = new FontLoader();
		return new Promise((resolve) => {
			loader.load('/line-assets/three-fonts/droid/droid_sans_mono_regular.typeface.json', (response) => {
				this.font = response;
				resolve(this.font);
			});
		});
	}

	async loadModels() {
		if (this.loaded) {
			return;
		}
		const source = await (new GLTFLoader().loadAsync('/line-assets/olivio3d/olivio.glb?20250604'));

		// the array because models can change their parent (and then disappear from the scene
		// children array) while being processed
		const models = [...source.scene.children];
		for (const designName of Object.keys(luminaires)) {
			const luminaireClass = luminaires[designName];
			luminaireClass.recognizeModels(models, !!this.staticView);
		}

		// for (const item of source.scene.children) {
		// 	if (!item.userData.modelType) {
		// 		const materialName = item.material ? item.material.name : '??';
		// 		console.log(item.name, ' > ', materialName);
		// 	}
		// }

		this.loaded = true;
	}

	// ACTIONS

	getCameraMovementData(posName, configValues) {
		let data = null;
		const DesignClass = luminaires[configValues.design];
		if (posName === 'side') {
			const pos = new Vector3(...conf.cameraPositions[configValues.design].side(configValues));
			const dir = new Vector3(0, pos.y, 0);
			data = {
				pos: pos,
				dir: dir,
			};
		} else if (posName === 'top') {
			const pos = new Vector3(...conf.cameraPositions[configValues.design].top(configValues));
			data = {
				pos: new Vector3(...pos),
				dir: new Vector3(0, pos.y - 3, 0),
			};
			if (configValues.currentSection === conf.detailModeSectionName) {
				data.rotationAngle = 0;
			}
		} else if (posName === 'detail') {
			const index = configValues.selectedHead;
			if (index === null) {
				throw new Error('Cannot move camera in detail mode without an selected head');
			}
			const headPos = new Vector3();
			const bracketPos = new Vector3();
			const branches = this.luminaire.getBranches();
			branches[index].head.getWorldPosition(headPos);
			const distanceFromOrigin = headPos.distanceTo(new Vector3(0, headPos.y, 0));
			let pos;
			let rotationAngle;
			if (DesignClass.singleBracket) {
				pos = new Vector3(-distanceFromOrigin, headPos.y + conf.detailVerticalOffset, conf.cameraConstraints.detailDistance);
				rotationAngle = degToRad(conf.detailAngle - DesignClass.getHeadAngle(index, DesignClass.getBracketId(configValues, index)));
			} else {
				branches[index].bracket.getWorldPosition(bracketPos);
				const offsets = this.luminaire.getVerticalOffsets()[index];
				const diff = headPos.y - bracketPos.y;
				pos = new Vector3(-distanceFromOrigin, offsets.actualHeight + diff + conf.detailVerticalOffset, conf.cameraConstraints.detailDistance);
				rotationAngle = degToRad(conf.detailAngle - configValues.heads[index].angle);
			}
			data = {
				pos: pos,
				dir: new Vector3(pos.x, pos.y, 0),
				rotationAngle: rotationAngle,
			};
		} else {
			console.log('UNDEFINED CAMERA POSITION', posName);
		}
		return data;
	}


	moveCamera({pos, configValues = null, disableCameraAutoMode = false}) {
		this.lastCameraPosName = pos;
		if (disableCameraAutoMode) {
			this.cameraAutoMode = false;
		}
		if (!configValues) {
			if (!this.luminaire) {
				return;
			}
			configValues = this.luminaire.getConfigValues();
		}
		const data = this.getCameraMovementData(pos, configValues);
		this.cameraPosition = data.pos;
		this.cameraTargetPosition = data.dir;

		if ('rotationAngle' in data) {
			this.poleTargetAngle = data.rotationAngle;
		}
	}

	moveDesignsCamera() {
		const design = this.selectingDesign ? this.currentDesign : null;
		let pos;
		let dir;
		if (!design) {
			const ratio = this.rect.width / this.rect.height;
			let maxHeight = 0;
			let minX = 0;
			let maxX = 0;
			for (const designEntry of Object.values(designsConfigs)) {
				const poleHeight = designEntry.configValues.poleHeight;
				const x = designEntry.position[0];
				maxHeight = Math.max(maxHeight, poleHeight);
				minX = Math.min(minX, x);
				maxX = Math.max(maxX, x);
			}
			const width = maxX - minX + 2; // leave 1 meter on each side
			const height = maxHeight - 1; // if we cut the tallest a bit on top is ok
			const halfWidth = width / 2;
			const halfHeight = height / 2;

			const distance = (ratio >= 1 ? halfHeight * ratio : halfWidth / ratio) + 0.2;

			pos = [0, halfHeight, distance];
			dir = [0, halfHeight, 0];
		} else {
			pos = conf.cameraPositions[design].side(designsConfigs[design].configValues);
			pos[0] = designsConfigs[design].position[0];
			dir = [pos[0], pos[1], 0];
		}
		this.designsCameraPosition = new Vector3(...pos);
		this.designsCameraTargetPosition = new Vector3(...dir);
	}

	moveStaticCamera() {
		let pos;
		let dir;
		const ratio = this.rect.width / this.rect.height;
		if (this.staticView === conf.staticViewSide) {
			const height = conf.staticViewSideHeight;
			const halfHeight = height / 2;
			const distance = halfHeight * (ratio > 1 ? ratio : 1);
			pos = [0, halfHeight, distance];
			dir = [0, halfHeight, 0];
		} else {
			const height = 20; // does not actually matter, it just needs to be bigger than the pole height
			const distance = 0.7 * (ratio > 1 ? ratio : 1);
			pos = [0, height, 0];
			dir = [0, height - distance, 0];
		}

		this.cameraPosition = new Vector3(...pos);
		this.cameraTargetPosition = new Vector3(...dir);
	}

	toggleAutorotation() {
		this.autorotate = !this.autorotate;
	}

	toggleCollisionBoxes() {
		this.showCollisionBoxes = !this.showCollisionBoxes;
		this.forceCollisionChecks = true;
	}

	toggleCameraMode(value = null) {
		if (value === null) {
			value = !this.cameraAutoMode;
		}
		this.cameraAutoMode = value;
		if (!value) {
			this.cameraPosition = null;
			this.cameraTargetPosition = null;
			this.poleTargetAngle = null;
		} else if (!this.inDesignsScene && this.lastCameraPosName) {
			this.moveCamera({pos: this.lastCameraPosName});
		}
	}


	autoUpdateCameraType() {
		if (this.staticView || this.inDesignsScene) {
			return;
		}
		const dir = new Vector3();
		this.camera.getWorldDirection(dir);
		this.setCameraType(dir.y <= conf.orthographicThreshold ? conf.cameraTypeOrthographic : conf.cameraTypePerspective);
	}


	setCameraType(cameraType) {
		const nextCamera = this.cameras[this.staticView ? conf.cameraTypeOrthographic : cameraType];
		const prevCamera = this.camera;
		if (prevCamera !== nextCamera) {
			nextCamera.position.copy(prevCamera.position);
			nextCamera.rotation.copy(prevCamera.rotation);
			this.camera = nextCamera;
			this.camera.updateProjectionMatrix();
			this.cameraControls.object = this.camera;
			this.cameraControls.enabled = !this.staticView && cameraType !== conf.cameraTypeDesigns;
			this.cameraControls.update();
			if (!this.staticView && cameraType !== conf.cameraTypeDesigns) {
				this.cameraType = cameraType;
			}
		}
	}

	toggleLights() {
		// const timeline = gsap.timeline();
		// this.lightOn = !this.lightOn;
		// const power = getLightPower(this.configValues.lumen, conf.defaultLightPower);
		// for (let i = 0; i < this.lights.length; i++) {
		// 	const light = this.lights[i];
		// 	const headLight = this.headLights[i];
		// 	timeline.to(light, {power: this.lightOn ? power : 0, duration: 0.8}, 0);
		// 	timeline.to(headLight, {power: this.lightOn ? conf.headLightPower : 0, duration: 0.8}, 0);
		// }
		// for (let i = 0; i < this.branches.length; i++) {
		// 	const branch = this.branches[i];
		// 	for (const reflector of branch.reflectors) {
		// 		timeline.to(reflector.material, {emissiveIntensity: this.lightOn ? conf.reflectorEmissionIntensity : 0, duration: 0.8}, 0);
		// 	}
		// }
	}


	// UTILS

	clearRenderer() {
		this.renderer.autoClear = false;
		this.renderer.clearDepth();
	}

	setCameraConstraints() {
		const controls = this.cameraControls;
		const constraints = conf.cameraConstraints;
		controls.enablePan = true;
		controls.enableRotate = true;
		controls.enableZoom = true;
		controls.update();
	}

	updateRendererGeometry() {
		this.renderer.setSize(this.rect.width, this.rect.height);
		this.renderer.domElement.style.width = `${this.rect.width}px`;
		this.renderer.domElement.style.height = `${this.rect.height}px`;
	}

	updateCameraGeometry() {
		const ratio = this.rect.width / this.rect.height;

		let w = 1;
		let h = 1;
		if (ratio >= 1) {
			h = w / ratio;
		} else {
			w = h * ratio;
		}

		this.cameras.perspective.aspect = ratio;
		for (const o of [this.cameras.orthographic, this.cameras.designs]) {
			o.left = -w / 2;
			o.right = w / 2;
			o.top = h / 2;
			o.bottom = -h / 2;
		}
		if (this.staticView) {
			this.moveStaticCamera();
		} else {
			if (this.inDesignsScene) {
				this.moveDesignsCamera();
			}
		}
	}

	processPointerIntersections() {
		this.raycaster.setFromCamera(this.pointer, this.camera);
		const intersects = this.raycaster.intersectObjects(this.scene.children);
		let pointedIndex = null;
		if (intersects.length) {
			intersects.sort((a, b) => a.distance - b.distance);
			for (const intersect of intersects) {
				if ('object' in intersect &&
					intersect.object &&
					intersect.object !== this.scene &&
					'hoverable' in intersect.object.userData &&
					intersect.object.userData.hoverable
				) {
					let obj = intersect.object;
					while (obj && !('index' in obj.userData)) {
						if (!obj.parent) {
							console.log('no parent', obj);
						}
						obj = obj.parent;
					}
					if (obj) {
						pointedIndex = obj.userData.index;
						break;
					}
				}
			}
		}
		if (this.pointedIndex !== pointedIndex) {
			const prevPointedIndex = this.pointedIndex;
			this.pointedIndex = pointedIndex;
			if (prevPointedIndex !== null) {
				this.onPointerOut(prevPointedIndex);
			}
			if (this.pointedIndex !== null) {
				this.onPointerIn(this.pointedIndex);
			}
		}
	}

	isDetailModeAllowed(state) {
		let detailModeAllowed = true;
		const sections = selectSectionConfig(state);
		for (const section of sections) {
			if (section.id === conf.detailModeSectionName) {
				break;
			}
			if (!isSectionSatisfied(section.id, state)) {
				detailModeAllowed = false;
				break;
			}
		}
		return detailModeAllowed;
	}

	updateLights() {
		if (this.staticView || !this.luminaire) {
			return;
		}
		const configValues = this.luminaire.getConfigValues();
		const color = this.luminaire.constructor.getColorForLightTuning(this.luminaire.getConfigValues());
		if (!this.lastColor || this.lastColor !== color) {
			const brightness = getColorSimpleBrightness(color);

			const baseBrightness = 0.4;
			const rightBaseIntensity = 3;
			const leftBaseIntensity = 1;
			const ambientBaseIntensity = 1.2;

			let ratio = brightness / baseBrightness;
			if (brightness < 0.1) {
				ratio *= 0.9;
			} else if (brightness > 0.6) {
				ratio = 1.1;
			}
			let ambientIntensity  = ambientBaseIntensity / ratio;
			if (brightness < 0.1) {
				ambientIntensity *= 1.7;
			} else if (brightness > 0.6) {
				ambientIntensity *= 0.7;
			}
			const rightIntensity  = rightBaseIntensity / ratio;
			const leftIntensity  = leftBaseIntensity / ratio;
			// console.log('COLOR', color, brightness.toFixed(3), ratio.toFixed(3), ambientIntensity.toFixed(3), rightIntensity.toFixed(3), leftIntensity.toFixed(3));
			this.ambientLight.intensity = ambientIntensity;
			this.rightDirectionalLight.intensity = rightIntensity;
			this.leftDirectionalLight.intensity = leftIntensity;
			this.lastColor = color;
		}

		// const verticalAngle = degToRad(getBranchVerticalAngle(this.configValues.poleHeight) + conf.headAngle);
		// for (let i = 0; i < this.lights.length; i++) {
		// 	const light = this.lights[i];
		// 	const pivot = this.lightTargets[i];
		// 	const target = pivot.children[0];

		// 	light.color.copy(lightColor);

		// 	if (i < this.branches.length && this.branches[i]) {
		// 		const branch = this.branches[i];
		// 		const lightLink = branch.lightLink;

		// 		if (light.parent !== this.scene) {
		// 			this.scene.add(light);
		// 		}
		// 		if (pivot.parent !== this.scene) {
		// 			this.scene.add(pivot);
		// 		}

		// 		const pos = new Vector3();
		// 		lightLink.getWorldPosition(pos);
		// 		light.position.copy(pos);

		// 		const angle = Math.atan2(pos.z, pos.x);
		// 		const d = pos.distanceTo(new Vector3(0, pos.y, 0));
		// 		const h = pos.y;
		// 		const b = d + (h * Math.tan(verticalAngle));
		// 		pivot.rotation.set(0, 0, 0);
		// 		target.position.set(b, 0.1, 0);
		// 		pivot.rotateY(-angle);
		// 	} else {
		// 		if (light.parent === this.scene) {
		// 			this.scene.remove(light);
		// 		}
		// 		if (pivot.parent === this.scene) {
		// 			this.scene.remove(pivot);
		// 		}
		// 	}
		// }
	}

	updateNumbers() {
		if (!this.luminaire) {
			return;
		}
		const configValues = this.luminaire.getConfigValues();
		const branches = this.luminaire.getBranches();
		const style = cssVars(this);
		for (let i = 0; i < branches.length; i++) {
			const number = branches[i].number;
			if (
				(this.currentSection === conf.detailModeSectionName && (configValues.selectedHead === i || this.pointedIndex === i)) ||
				(this.currentSection !== conf.detailModeSectionName && this.detailModeAllowed && this.pointedIndex === i)
			) {
				const numberPos = new Vector3();
				number.getWorldPosition(numberPos);
				this.camera.updateProjectionMatrix();
				this.camera.updateMatrixWorld();
				numberPos.project(this.camera);
				const screenPos = {
					x: (numberPos.x + 1) * this.rect.width / 2,
					y: (-numberPos.y + 1) * this.rect.height / 2
				};

				style.set('number' + (i + 1) + 'x', screenPos.x + 'px');
				style.set('number' + (i + 1) + 'y', screenPos.y + 'px');
				style.set('number' + (i + 1) + 'opacity', 1);
			} else {
				style.remove('number' + (i + 1) + 'opacity');
			}
		}
	}

	updateCollisionsBoxes() {
		if (!this.luminaire) {
			return null;
		}

		const helpers = [
			{prefix: 'head', objectName: 'head'},
			{prefix: 'bracket', objectName: 'bracketMesh'},
		];

		const boxes = {
			head: [],
			bracket: []
		};

		// preparing boxes
		const branches = this.luminaire.getBranches();
		const configValues = this.luminaire.getConfigValues();
		for (let i = 0; i < conf.maxHeadsAmount; i++) {
			const branch = branches[i];
			const head = configValues.heads[i];
			if (head.active) {
				branch.bracket.updateMatrix();
				branch.bracket.updateMatrixWorld();
				branch.bracketMesh.updateMatrix();
				branch.bracketMesh.updateMatrixWorld();
				branch.head.updateMatrix();
				branch.head.updateMatrixWorld();

				for (const entry of helpers) {
					const prefix = entry.prefix;
					const helper = this[prefix + 'BoxHelpers'][i];
					helper.visible = this.showCollisionBoxes;
					helper.scale.copy(branch[prefix + 'Size']);
					helper.position.copy(branch[prefix + 'Offset']);
					helper.rotation.set(0, 0, 0);
					helper.applyMatrix4(branch[entry.objectName].matrixWorld);
					helper.material.color.set(conf.boxHelperColor);

					const box = new OBB();
					box.halfSize.set(0.5, 0.5, 0.5);
					box.applyMatrix4(helper.matrixWorld);
					boxes[prefix][i] = box;
				}
			} else {
				for (const entry of helpers) {
					const prefix = entry.prefix;
					this[prefix + 'BoxHelpers'][i].visible = false;
					boxes[prefix][i] = null;
				}
			}
		}
		return boxes;
	}


	checkCollisions(boxes) {
		if (!boxes) {
			return;
		}

		const helpers = [
			{prefix: 'head', objectName: 'head'},
			{prefix: 'bracket', objectName: 'bracketMesh'},
		];

		// checking collisions
		const collisions = Array.from({length: conf.maxHeadsAmount}, () => (new Set()));
		for (let i = 0; i < conf.maxHeadsAmount; i++) {
			for (let j = i + 1; j < conf.maxHeadsAmount; j++) {
				if (i === j) {
					continue;
				}
				for (const entry of helpers) {
					const prefix = entry.prefix;
					const box = boxes[prefix][i];
					const otherBox = boxes[prefix][j];

					if (box !== null && otherBox !== null && box !== otherBox && box.intersectsOBB(otherBox)) {
						collisions[i].add(j);
						collisions[j].add(i);
						this[prefix + 'BoxHelpers'][i].material.color.set(conf.boxHelperOnCollisionColor);
						this[prefix + 'BoxHelpers'][j].material.color.set(conf.boxHelperOnCollisionColor);
					}
				}
			}
		}

		const configValues = this.luminaire.getConfigValues();
		// checking for changes
		let changed = false;
		for (let i = 0; i < conf.maxHeadsAmount; i++) {
			const prevCollisions = configValues.heads[i].collisions;
			const currentCollisions = [...collisions[i]].sort((a, b) => a - b);
			collisions[i] = currentCollisions;
			if (!changed) {
				if (prevCollisions.length !== currentCollisions.length) {
					changed = true;
					continue;
				} else {
					for (let j = 0; j < prevCollisions.length; j++) {
						if (prevCollisions[j] !== currentCollisions[j]) {
							changed = true;
							break;
						}
					}
				}
			}
		}

		// console.log('Checked Collisions', changed);
		this.forceCollisionChecks = false;
		if (changed) {
			store.dispatch(setOlivioHeadsData({heads: collisions.map((headCollisions) => ({collisions: headCollisions}))}));
		}
	}


	updateCameraMovement(elapsedTime) {
		let changed = false;
		const translateRatio = Math.min(1, 0.07 * elapsedTime * 60 / 1000);
		const minDistance = 0.001;

		const entries = [];
		if (this.cameraPosition) {
			entries.push(
				{final: this.cameraPosition, current: this.cameras.perspective.position},
				{final: this.cameraPosition, current: this.cameras.orthographic.position},
			);
		}
		if (this.cameraTargetPosition || (this.inDesignsScene && this.designsCameraTargetPosition)) {
			entries.push({final: !this.staticView && this.inDesignsScene ? this.designsCameraTargetPosition : this.cameraTargetPosition, current: this.cameraControls.target});
		}
		if (this.designsCameraPosition) {
			entries.push({final: this.designsCameraPosition, current: this.cameras.designs.position});
		}

		if (entries.length) {
			for (const entry of entries) {
				const overallDistance = entry.final.distanceTo(entry.current);
				if (overallDistance > minDistance) {
					changed = true;
					const diff = overallDistance * translateRatio;
					const alpha = this.staticView || (overallDistance <= minDistance) ? 1 : diff / overallDistance;
					entry.current.lerp(entry.final, alpha);
				} else {
					entry.current.lerp(entry.final, 1);
				}
			}
			if (this.cameraTargetPosition) {
				const distance = this.cameras.orthographic.position.distanceTo(this.cameraTargetPosition);
				this.cameras.orthographic.zoom = 1 / (distance * 2);
			}

			if (this.designsCameraTargetPosition) {
				const distanceDesigns = this.cameras.designs.position.distanceTo(this.designsCameraTargetPosition);
				this.cameras.designs.zoom = 1 / (distanceDesigns * 2);
			}
		}
		this.autoUpdateCameraType();
		return changed;
	}

	updatePoleRotation(elapsedTime) {
		let changed = false;
		if (!this.staticView) {
			const inDesignsScene = this.currentSection === conf.designsSectionName;
			if (inDesignsScene || (this.luminaire && this.luminaire.getPole() && this.luminaire.getPole().parent === this.scene)) {
				let pole = null;
				if (inDesignsScene) {
					if (this.currentDesign) {
						for (const model of this.designsScene.children) {
							if (model.userData && model.userData.design && model.userData.design === this.currentDesign) {
								pole = model;
								break;
							}
						}
					}
				} else {
					pole = this.luminaire.getPole();
				}
				const rotateRatio = Math.min(1, 0.08 * elapsedTime * 60 / 1000);
				const minAngle = degToRad(1);
				const baseRotationAngle = Math.min(1, 0.007 * elapsedTime * 60 / 1000);

				if (!inDesignsScene && this.poleTargetAngle !== null) {
					const quaternion = getQuaternionByAngle(0, this.poleTargetAngle, 0);
					const angleDistance = pole.quaternion.angleTo(quaternion);
					if (angleDistance > 0) {
						changed = true;
						pole.quaternion.rotateTowards(
							quaternion,
							angleDistance <= minAngle ? angleDistance : angleDistance * rotateRatio
						);
					} else {
						this.poleTargetAngle = null;
					}
				} else if (
					(inDesignsScene && pole) || (
						this.autorotate &&
						!this.flatView &&
						(this.currentSection !== conf.detailModeSectionName || !this.luminaire.constructor.hasBrackets)
					)
				) {
					if (pole) {
						changed = true;
						pole.rotateY(baseRotationAngle);
					}
				}
			}
		}
		return changed;
	}


	// ANIMATE LOOP & RENDER

	animate() {
		if (!this.isConnected) {
			return;
		}
		const currentTime = performance.now();
		const elapsedTime = currentTime - this.lastTime;

		if (!this.inDesignsScene) {
			if (this.luminaire) {
				const DesignClass = this.luminaire.constructor;
				const poleChanged = this.updatePoleRotation(elapsedTime);

				if (DesignClass.hasBrackets) {
					const headsChanged = this.luminaire.updateHeadsPositions(this.staticView ? null : elapsedTime);

					if (!this.staticView && DesignClass.checkCollisions) {
						if (this.forceCollisionChecks || headsChanged || (this.showCollisionBoxes && poleChanged)) {
							const boxes = this.updateCollisionsBoxes();
							if (this.forceCollisionChecks || headsChanged) {
								this.checkCollisions(boxes);
							}
						}
					}

					if (DesignClass.requireCutouts) {
						const poleReplaced = this.luminaire.updatePoleCutouts();
						if (poleReplaced) {
							this.scene.add(this.luminaire.getPole());
						}
					}
				}
			}
		} else {
			const poleChanged = this.updatePoleRotation(elapsedTime);
		}

		this.updateCameraMovement(elapsedTime);

		this.camera.updateProjectionMatrix();
		this.cameraControls.update();

		this.processPointerIntersections();

		this.updateLights();
		this.updateNumbers();

		if (this.inDesignsScene) {
			this.renderer.render(this.designsScene, this.camera);
		} else {
			this.renderer.render(this.scene, this.camera);
		}
		this.lastTime = currentTime;
		requestAnimationFrame(() => this.animate());
	}

	captureCanvasImage() {
		try {
			this.renderer.render(this.scene, this.camera); // rerender to make sure render buffer is ready
			const dataUrl = this.renderer.domElement.toDataURL('image/png');
			store.dispatch(saveCanvasImage({
				dataUrl: dataUrl,
				viewType: this.staticView
			}));
		} catch (error) {
			console.error('Error capturing canvas image:', error);
		}
	}

	render() {
		const s = this.state;
		return html`
			${this.renderer.domElement}
			${when(!this.staticView, () => html`
				<div class="controls">
					${when(this.loaded && !this.inDesignsScene, () => html`
						<button type="button" class="action" @click=${this.onAction} .action=${'moveCamera'} .actionParams=${{pos: 'top', disableCameraAutoMode: true}}>
							<span class="actionLabel">top view</span>
							<span class="actionIcon">${iconTopView}</span>
						</button>
						<button type="button" class="action" @click=${this.onAction} .action=${'moveCamera'} .actionParams=${{pos: 'side', disableCameraAutoMode: true}}>
							<span class="actionLabel">side view</span>
							<span class="actionIcon">${iconSideView}</span>
						</button>
						<button type="button" class="action" @click=${this.onAction} .action=${'toggleAutorotation'}>
							<span class="actionLabel">${this.autorotate && this.currentSection !== conf.detailModeSectionName ? 'rotation on' : 'rotation off'}</span>
							<span class="actionIcon">${this.autorotate && this.currentSection !== conf.detailModeSectionName ? iconRotationOn : iconRotationOff}</span>
						</button>
						<button type="button" class="action" @click=${this.onAction} .action=${'toggleCameraMode'}>
							<span class="actionLabel">${this.cameraAutoMode ? 'auto camera' : 'manual camera'}</span>
							<span class="actionIcon">${this.cameraAutoMode ? iconAutoView : iconManualView}</span>
						</button>
					`)}
				</div>
				<div class="numbers">
					${repeat(Array.from({length: conf.maxHeadsAmount}, (v, i) => (i + 1)), (number) => html`
						<span class="number">${number}</span>
					`)}
				</div>
			`)}
			${when(selectDebug(s), () => html`
				<div class="debug">
					<button type="button" class="action" @click=${this.onAction} .action=${'toggleCollisionBoxes'}>Toggle Collision Boxes</button>
					<!--
					<button type="button" class="action" @click=${this.onAction} .action=${'toggleLights'}>Toggle Lights</button>
					-->
				</div>
			`)}
		`;
	}

}
